import { Controller } from "@hotwired/stimulus";
let intlTelInputLib = null;

export default class PhoneNumberController extends Controller {

  initialize() {
    this.loadLib().then(() => {
      const countryCode = this.element.dataset.countryCode ||
        document.querySelector(".country_code")?.value ||
        "us";
      this.setPlaceholder(countryCode);
      this.setupPhoneInput();
    });
  }

  connect() {
    this.setupFormValidation();
  }

  async loadLib() {
    if (intlTelInputLib) return;

    const module = await import("intl-tel-input/intlTelInputWithUtils");
    intlTelInputLib = module.default || module;
  }

  setupPhoneInput() {
    const intlTelInput = intlTelInputLib;

    // Turbo may restore widget markup without its JavaScript instance.
    let cachedWrapper;
    while ((cachedWrapper = this.element.closest(".iti"))) {
      cachedWrapper.replaceWith(this.element);
    }

    this.handleInput = (event) => {
      event.target.value = event.target.value.replaceAll(/[a-zA-Z]/g, "");
      this.applyPhoneMask(event);
    };

    const countryCode = this.getInitialCountryCode(intlTelInput);

    this.errorMap = [
      "Invalid number",
      "Invalid country code",
      "Phone number is too short",
      "Phone number is too long",
      "Invalid number"
    ];

    const iti = intlTelInput(this.element, {
      initialCountry: countryCode,
      separateDialCode: true,
      strictMode: true,
      utilsScript: intlTelInput.utils,
      formatOnDisplay: true,
      countrySearch: true,
      dropdownContainer: this.element.closest(".modal"),
      autoInsertDialCode: false,
      nationalMode: false
    });

    this.iti = iti;
    this.currentCountry = countryCode;

    this.setupCountryChangeHandler();
    this.setupCountryDropdownHandler();
    this.setupInputHandler();
    this.setupBlurHandler(iti);
    this.setupChangeHandler(iti);

    requestAnimationFrame(() => {
      if (this.element.value.trim()) {
        this.applyPhoneMask({ target: this.element });
      }
    });

    if (this.element.closest(".sign-up-form")) {
      this.enableSubmit();
    }
  }

  getInitialCountryCode(intlTelInput) {
    const dialCode = this.element.dataset.countryCode ||
      document.querySelector(".country_code")?.value ||
      "us";
    const countryData = intlTelInput.getCountryData();

    return countryData.find((country) => country.iso2 === dialCode)?.iso2 || dialCode;
  }

  setupCountryDropdownHandler() {
    this.element.addEventListener("open:countrydropdown", () => {
      this.userSelectingCountry = true;
    });
  }

  setupInputHandler() {
    this.element.addEventListener("input", (event) => {
      this.handleInput(event);

      const digits = this.element.value.replace(/\D/g, "");
      const maxDigits = (this.element.placeholder.match(/X/g) || []).length;

      if (digits.length > maxDigits) {
        const limitedDigits = digits.slice(0, maxDigits);
        this.element.value = "";

        this.applyPhoneMask({
          target: Object.assign(this.element, { value: limitedDigits })
        });
      }

      this.validateInput();
    });
  }

  setupChangeHandler(iti) {
    this.element.addEventListener("change", () => {
      this.reset(iti);
    });
  }

  setupCountryChangeHandler() {
    this.element.addEventListener("countrychange", () => {
      const selectedCountry = this.iti.getSelectedCountryData().iso2;

      if (this.userSelectingCountry) {
        this.handleUserCountrySelection(selectedCountry);
        return;
      }

      requestAnimationFrame(() => {
        this.restoreCountrySelection();
      });
    });
  }

  handleUserCountrySelection(selectedCountry) {
    this.currentCountry = selectedCountry;
    this.userSelectingCountry = false;

    this.updateCountryCode(selectedCountry);
    this.setPlaceholder(selectedCountry);
  }

  restoreCountrySelection() {
    this.iti.setCountry(this.currentCountry);
    this.updateCountryCode(this.currentCountry);
    this.setPlaceholder(this.currentCountry);

    if (this.element.value.trim()) {
      this.applyPhoneMask({ target: this.element });
    }
  }

  updateCountryCode(countryCode) {
    document.querySelector(".country_code").value = countryCode;
  }

  setupBlurHandler(iti) {
    this.element.addEventListener("blur", () => {
      this.handleBlur(iti);
    });
  }

  handleBlur(iti) {
    this.reset(iti);

    if (!this.element.value) return;

    const form = this.element.closest("form");
    const submitButton = form.querySelector("button[type=\"submit\"]");

    this.updateCountryCode(this.currentCountry);

    if (iti.isValidNumber()) {
      this.handleValidPhone(form, submitButton);
      return;
    }

    this.handleInvalidPhone(iti, form, submitButton);
  }

  handleValidPhone(form, submitButton) {
    if (form.classList.contains("edit-profile")) {
      this.element.dispatchEvent(
        new Event("input", { bubbles: true })
      );
      return;
    }

    this.enableSubmit();
  }

  handleInvalidPhone(iti, form, submitButton) {
    this.element.classList.add("error");

    const errorCode = iti.getValidationError();
    const errorMessage = this.errorMap[errorCode] || "Invalid number";

    this.showError(errorMessage);

    if (form.classList.contains("edit-profile")) {
      this.notifyFormChange(form);
      return;
    }

    submitButton.disabled = true;
  }

  notifyFormChange(form) {
    const formChangeController = form.querySelector(
      "[data-controller*=\"form-change\"]"
    );

    if (!formChangeController) return;

    formChangeController.dispatchEvent(
      new Event("input", { bubbles: true })
    );
  }


  reset(iti) {
    this.element.classList.remove("error");

    if (typeof intlTelInputUtils !== "undefined") {
      const currentFormat = iti.getNumber(
        intlTelInputUtils.numberFormat.INTERNATIONAL
      );
      if (typeof currentFormat === "string") {
        iti.setNumber(currentFormat);
      }
    }
    this.applyPhoneMask({ target: this.element });
  }

  validateInput() {
    this.resetErrors();

    if (this.element.disabled) return;
    if (!this.element.value.trim()) return;

    if (this.iti.isValidNumber()) {
      this.enableSubmit();
    } else {
      this.showError(this.errorMap[this.iti.getValidationError()]);
      this.disableSubmit();
    }
  }

  enableSubmit() {
    const form = this.element.closest("form");
    if (form.classList.contains("sign-up-form")) {
      const signupController = this.application.getControllerForElementAndIdentifier(form, "onboarding-signup");
      signupController?.toggleSubmitButton();
      return;
    }

    const btn = this.element
      .closest("form")
      .querySelector("button[type=\"submit\"]");
    if (btn) btn.disabled = false;
  }

  disableSubmit() {
    const btn = this.element
      .closest("form")
      .querySelector("button[type=\"submit\"]");
    if (btn) btn.disabled = true;
  }

  setupFormValidation() {
    const form = this.element.closest("form");

    form.addEventListener("change", (e) => {
      if (this.element.disabled || !this.iti) return;

      if (!this.iti.isValidNumber()) {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.validateInput();
        this.applyPhoneMask({ target: this.element });
      }
    });
  }

  showError(msg) {
    this.element.classList.add("error");
    const errorContainer = this.element
      .closest(".form-group")
      .querySelector(".error-msg");
    if (errorContainer) errorContainer.textContent = msg;
  }

  resetErrors() {
    this.element.classList.remove("error");
    const errorMsg = this.element
      .closest(".form-group")
      .querySelector(".error-msg");
    if (errorMsg) errorMsg.textContent = "";

    if (typeof intlTelInputUtils !== "undefined") {
      const formatted = this.iti.getNumber(
        intlTelInputUtils.numberFormat.INTERNATIONAL
      );
      if (formatted) this.iti.setNumber(formatted);
    }
  }

  applyPhoneMask(event) {
    const el = event.target;
    const mask = el.placeholder || "";
    const digits = el.value.replaceAll(/\D/g, "");

    if (!digits) {
      el.value = "";
      return;
    }

    let result = "";
    let digitIndex = 0;
    for (const ch of mask) {
      if (digitIndex >= digits.length) break;

      if (ch === "X") {
        result += digits[digitIndex++];
        continue;
      }

      result += ch;
    }

    // Do not discard extra digits. intl-tel-input needs to receive the complete
    // value in order to return its "Phone number is too long" validation error.
    if (digitIndex < digits.length) result += digits.slice(digitIndex);

    const newCursorPos = this.setCursorPosition(
      el.selectionStart,
      el.value,
      result
    );
    el.value = result;
    el.setSelectionRange(newCursorPos, newCursorPos);
  }

  setCursorPosition(selectionStart, oldValue, newValue) {
    let newCursorPos = 0;
    let digitsCounted = 0;
    const digitsBeforeCursor = oldValue
      .slice(0, selectionStart)
      .replaceAll(/\D/g, "").length;

    for (
      let i = 0;
      i < newValue.length && digitsCounted < digitsBeforeCursor;
      i++
    ) {
      newCursorPos++;
      if (/\d/.test(newValue[i])) {
        digitsCounted++;
      }
    }
    return newCursorPos;
  }

  setPlaceholder(countryCode) {
    if (!intlTelInputLib) return;

    const utils = intlTelInputLib.utils;

    const exampleNumber = utils?.getExampleNumber?.(
      countryCode?.toUpperCase?.(),
      true,
      utils?.numberFormat?.NATIONAL
    );

    if (!exampleNumber) return;

    this.element.placeholder = exampleNumber.replaceAll(/\d/g, "X");

    if ($("#user_phone_no").html() === "\"undefined\".") {
      $("#user_phone_no").html(`"${exampleNumber}".`);
    }
  }
}
